import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ENTITY_TYPE_ITEMS } from 'lib/entities';
import {
  syncCreatedAtToUrl,
  syncUpdatedAtToUrl,
  syncEntityTypesToUrl,
  syncOperatorOnlyToUrl,
  syncSearchToUrl,
  syncSortToUrl,
  syncPaginationToUrl,
  syncEntityTypeFilterToUrl,
  syncDeduplicatedToUrl,
  clearFilterParams,
} from './atoms';
import {
  useEntitySearchHandlers,
  type SetEntitySearchPayload,
} from 'components/entity-search-modal';
import type { DateRange, EntityType } from './types';

/**
 * The list's search changes: each applies to the search as the entity search
 * modal's do and is mirrored in the URL.
 * @param {SetEntitySearchPayload} setSearchPayload Updates the search.
 * @return {object} The filter, search, sort and pagination handlers.
 */
export function useEntityListSearchHandlers(
  setSearchPayload: SetEntitySearchPayload
) {
  const history = useHistory();
  const location = useLocation();
  const {
    onCreatedAtChange,
    onUpdatedAtChange,
    onEntityTypesChange,
    onEntityTypeFilterChange,
    onSearchChange,
    onSortChange,
    onPaginationChange,
  } = useEntitySearchHandlers(setSearchPayload, null);

  const handleCreatedAtChange = useCallback(
    (createdAt?: DateRange) => {
      onCreatedAtChange(createdAt);
      syncCreatedAtToUrl(createdAt, location, history);
    },
    [location, history, onCreatedAtChange]
  );

  const handleUpdatedAtChange = useCallback(
    (updatedAt?: DateRange) => {
      onUpdatedAtChange(updatedAt);
      syncUpdatedAtToUrl(updatedAt, location, history);
    },
    [location, history, onUpdatedAtChange]
  );

  const handleEntityTypesChange = useCallback(
    (types: EntityType[]) => {
      onEntityTypesChange(types);
      syncEntityTypesToUrl(types, location, history);
    },
    [location, history, onEntityTypesChange]
  );

  const handleOperatorOnlyChange = useCallback(
    (checked: boolean) => {
      setSearchPayload((current) => ({
        ...current,
        page: 1,
        // Unchecked is no filter.
        operatorOnly: checked || undefined,
      }));
      syncOperatorOnlyToUrl(checked, location, history);
    },
    [location, history, setSearchPayload]
  );

  const handleResetFilters = useCallback(() => {
    setSearchPayload((current) => ({
      ...current,
      page: 1,
      createdAt: undefined,
      updatedAt: undefined,
      entityTypes: ENTITY_TYPE_ITEMS,
      operatorOnly: undefined,
      includePrimary: true,
      includeAliases: true,
      includeDeduplicated: false,
    }));
    clearFilterParams(location, history);
  }, [location, history, setSearchPayload]);

  const handleSearchChange = useCallback(
    (text: string) => {
      onSearchChange(text);
      syncSearchToUrl(text || undefined, location, history);
    },
    [location, history, onSearchChange]
  );

  const handleSortChange = useCallback(
    (
      sortAttribute: string | undefined,
      sortDirection: 'ASC' | 'DESC' | undefined
    ) => {
      onSortChange(sortAttribute, sortDirection);
      syncSortToUrl(sortAttribute, sortDirection, location, history);
    },
    [location, history, onSortChange]
  );

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      onPaginationChange(page, pageSize);
      syncPaginationToUrl(page, pageSize, location, history);
    },
    [location, history, onPaginationChange]
  );

  const handleEntityTypeFilterChange = useCallback(
    (includePrimary: boolean, includeAliases: boolean) => {
      onEntityTypeFilterChange(includePrimary, includeAliases);
      syncEntityTypeFilterToUrl(
        includePrimary,
        includeAliases,
        location,
        history
      );
    },
    [location, history, onEntityTypeFilterChange]
  );

  const handleDeduplicatedChange = useCallback(
    (includeDeduplicated: boolean) => {
      setSearchPayload((current) => ({
        ...current,
        page: 1,
        includeDeduplicated,
      }));
      syncDeduplicatedToUrl(includeDeduplicated, location, history);
    },
    [location, history, setSearchPayload]
  );

  return {
    handleCreatedAtChange,
    handleUpdatedAtChange,
    handleEntityTypesChange,
    handleOperatorOnlyChange,
    handleResetFilters,
    handleSearchChange,
    handleSortChange,
    handlePaginationChange,
    handleEntityTypeFilterChange,
    handleDeduplicatedChange,
  };
}
